import { useState } from 'react';
import { Menu, Bell, LogOut, User } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { isPremiumPlan, planLabel } from '../../lib/plans';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

interface NavbarProps {
  onMenuClick: () => void;
}

export default function Navbar({ onMenuClick }: NavbarProps) {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const isPremium = isPremiumPlan(profile?.plan);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch {
      toast.error('Failed to sign out');
    }
  };

  return (
    <header className="sticky top-0 z-20 bg-slate-950/80 backdrop-blur-md border-b border-slate-800 px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
      {/* Left: hamburger (mobile) + page context */}
      <button
        onClick={onMenuClick}
        className="lg:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
        aria-label="Open menu"
      >
        <Menu size={20} />
      </button>

      <div className="hidden lg:block" />

      {/* Right: actions */}
      <div className="flex items-center gap-3">
        {/* Plan Badge */}
        {profile && (
          <span className={`hidden sm:flex items-center px-2.5 py-1 text-xs font-semibold rounded-full border ${
            isPremium
              ? 'border-brand-500/30 text-brand-400 bg-brand-500/10 shadow-[0_0_10px_rgba(var(--brand-500),0.1)]' 
              : 'border-slate-700 text-slate-400 bg-slate-800/50'
          }`}>
            {isPremium ? `🏆 ${planLabel(profile.plan)}` : 'Free Plan'}
          </span>
        )}

        {/* Notifications placeholder */}
        <button
          className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors relative"
          aria-label="Notifications"
        >
          <Bell size={18} />
        </button>

        {/* Avatar / dropdown */}
        <div className="relative">
          <button
            onClick={() => setDropdownOpen(v => !v)}
            className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-slate-800 transition-colors"
            aria-haspopup="true"
            aria-expanded={dropdownOpen}
          >
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-500 to-violet-600 flex items-center justify-center text-xs font-bold text-white">
              {(profile?.full_name ?? profile?.email ?? 'U').charAt(0).toUpperCase()}
            </div>
            <span className="hidden sm:block text-sm font-medium text-slate-300 max-w-[120px] truncate">
              {profile?.full_name ?? profile?.email ?? 'User'}
            </span>
          </button>

          {dropdownOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setDropdownOpen(false)}
                aria-hidden="true"
              />
              <div className="absolute right-0 mt-2 w-48 card shadow-xl z-20 py-1 animate-fade-in">
                <button
                  onClick={() => { setDropdownOpen(false); navigate('/settings'); }}
                  className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
                >
                  <User size={15} />
                  Profile & Settings
                </button>
                <hr className="border-slate-800 my-1" />
                <button
                  onClick={() => { setDropdownOpen(false); handleSignOut(); }}
                  className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-slate-800 transition-colors"
                >
                  <LogOut size={15} />
                  Sign Out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
